export type CapacityViolation = {
  kind: "day" | "size" | "room" | "bed";
  label: string;
  used: number;
  limit: number;
};

export type CapacityReservation = {
  moduleType: "daycare" | "hotel" | "both";
  stayDate: string;
  size: "small" | "medium" | "large" | null;
  roomId: string | null;
  bedId: string | null;
};

export type CapacityLimits = {
  day: number | null;
  limitBySize: boolean;
  bySize: Partial<Record<"small" | "medium" | "large", number | null>>;
  room: Record<string, { label: string; limit: number }>;
  bed: Record<string, { label: string; limit: number }>;
};

const covers = (reservation: CapacityReservation, date: string) => reservation.stayDate === date;

const appliesToModule = (reservation: CapacityReservation, module: "daycare" | "hotel") =>
  reservation.moduleType === module || reservation.moduleType === "both";

export function findCapacityViolations(args: {
  module: "daycare" | "hotel";
  dates: string[];
  size: CapacityReservation["size"];
  roomId: string | null;
  bedId: string | null;
  existing: CapacityReservation[];
  limits: CapacityLimits;
}): CapacityViolation[] {
  const violations: CapacityViolation[] = [];
  for (const date of args.dates) {
    const occupying = args.existing.filter((reservation) => covers(reservation, date) && appliesToModule(reservation, args.module));
    if (args.limits.day !== null && occupying.length + 1 > args.limits.day) {
      violations.push({ kind: "day", label: "day", used: occupying.length + 1, limit: args.limits.day });
    }
    if (args.module === "daycare" && args.limits.limitBySize && args.size) {
      const limit = args.limits.bySize[args.size] ?? null;
      const used = occupying.filter((reservation) => reservation.size === args.size).length + 1;
      if (limit !== null && used > limit) violations.push({ kind: "size", label: args.size, used, limit });
    }
    if (args.module === "hotel" && args.roomId) {
      const room = args.limits.room[args.roomId];
      const used = occupying.filter((reservation) => reservation.roomId === args.roomId).length + 1;
      if (room && used > room.limit) violations.push({ kind: "room", label: room.label, used, limit: room.limit });
    }
    if (args.module === "hotel" && args.bedId) {
      const bed = args.limits.bed[args.bedId];
      const used = occupying.filter((reservation) => reservation.bedId === args.bedId).length + 1;
      if (bed && used > bed.limit) violations.push({ kind: "bed", label: bed.label, used, limit: bed.limit });
    }
  }
  return violations.filter((violation, index, all) => all.findIndex((candidate) => candidate.kind === violation.kind && candidate.label === violation.label) === index);
}
